import { useEffect, useRef, useState } from 'react';
import { buildAddressLine1, type GeoapifyAddressProperties, type GeoapifySuggestion } from '../types/geoapify';

const API_KEY = import.meta.env.VITE_GEOAPIFY_API_KEY as string;
const DEBOUNCE_MS = 200;

interface AddressAutocompleteProps {
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  value?: string;
  onPlaceSelect?: (place: GeoapifySuggestion) => void;
  onChange?: (value: string) => void;
  filterByCountryCode?: string[];
}

const COUNTRY_SUFFIX_PATTERN = /, (United States of America|USA)$/;

function stripCountrySuffix(value: string): string {
  return value.replace(COUNTRY_SUFFIX_PATTERN, '');
}

function formatSuggestionDisplay(properties?: GeoapifyAddressProperties) {
  const formatted = properties?.formatted ?? properties?.address_line1 ?? '';
  const addressLine2 = properties?.address_line2 ?? '';

  return {
    main: stripCountrySuffix(formatted),
    secondary: stripCountrySuffix(addressLine2),
  };
}

// AddressAutocomplete wraps the Geoapify GeocoderAutocomplete SDK.
// When `label` is provided, it renders Mantine-compatible label markup so it can
// stand in directly as the "Address Line 1" field with autocomplete wired in.
export default function AddressAutocomplete({
  placeholder = 'Enter address',
  label,
  required,
  error,
  value = '',
  onPlaceSelect,
  onChange,
  filterByCountryCode,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const latestOnChangeRef = useRef(onChange);
  const latestOnPlaceSelectRef = useRef(onPlaceSelect);
  const allowLookupRef = useRef(false);
  const [suggestions, setSuggestions] = useState<GeoapifySuggestion[]>([]);

  const hideSuggestions = () => setSuggestions([]);

  useEffect(() => {
    latestOnChangeRef.current = onChange;
    latestOnPlaceSelectRef.current = onPlaceSelect;
  }, [onChange, onPlaceSelect]);

  useEffect(() => {
    if (!allowLookupRef.current) {
      hideSuggestions();
      return;
    }

    const trimmedValue = value.trim();
    if (!API_KEY || trimmedValue.length < 3) {
      hideSuggestions();
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const url = new URL('https://api.geoapify.com/v1/geocode/autocomplete');
        url.searchParams.set('text', trimmedValue);
        url.searchParams.set('limit', '5');
        url.searchParams.set('apiKey', API_KEY);
        if (filterByCountryCode && filterByCountryCode.length > 0) {
          url.searchParams.set('filter', `countrycode:${filterByCountryCode.join(',')}`);
        }

        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) {
          hideSuggestions();
          return;
        }

        const body = await response.json() as { features?: GeoapifySuggestion[] };
        setSuggestions(Array.isArray(body.features) ? body.features : []);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          hideSuggestions();
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [filterByCountryCode, value]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    if (error) {
      input.setAttribute('data-error', 'true');
      input.setAttribute('aria-invalid', 'true');
    } else {
      input.removeAttribute('data-error');
      input.removeAttribute('aria-invalid');
    }
  }, [error]);

  const handleSuggestionSelect = (suggestion: GeoapifySuggestion) => {
    const addressLine1 = buildAddressLine1(suggestion.properties);

    allowLookupRef.current = false;
    hideSuggestions();
    latestOnChangeRef.current?.(addressLine1);
    latestOnPlaceSelectRef.current?.(suggestion);
    inputRef.current?.focus();
  };

  return (
    <div className={`geoapify-field-wrapper${error ? ' geoapify-field-wrapper--error' : ''}`}>
      {label && (
        <label
          className="geoapify-field-label mantine-InputWrapper-label"
          data-size="sm"
          data-required={required ? 'true' : undefined}
          style={{ '--input-label-size': 'var(--mantine-font-size-sm)' } as React.CSSProperties}
        >
          {label}
          {required && <span className="geoapify-field-required"> *</span>}
        </label>
      )}
      <div className="geoapify-autocomplete-shell">
        <input
          ref={inputRef}
          className="geoapify-autocomplete-input"
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(event) => {
            allowLookupRef.current = true;
            latestOnChangeRef.current?.(event.target.value);
          }}
          onBlur={() => {
            allowLookupRef.current = false;
            window.setTimeout(() => hideSuggestions(), 150);
          }}
          data-error={error ? 'true' : undefined}
          aria-invalid={error ? 'true' : undefined}
        />
        {suggestions.length > 0 && (
          <div className="geoapify-suggestions-dropdown">
            {suggestions.map((suggestion, index) => {
              const display = formatSuggestionDisplay(suggestion.properties);

              return (
                <button
                  key={`${display.main}-${index}`}
                  type="button"
                  className="geoapify-suggestion-item"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSuggestionSelect(suggestion);
                  }}
                >
                  <span className="geoapify-suggestion-main">{display.main}</span>
                  {display.secondary && (
                    <span className="geoapify-suggestion-secondary">{display.secondary}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {error && <div className="mantine-InputWrapper-error">{error}</div>}
      <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'inherit', opacity: 0.6 }}>
        Powered by{' '}
        <a
          href="https://www.geoapify.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'none', borderBottom: '1px dotted currentColor' }}
        >
          Geoapify
        </a>
      </p>
    </div>
  );
}
